import React, { useState, useEffect, useRef } from 'react';
import { get, post, put } from '../../services/apiController';
import { TABLE_DEFAULT_LIMIT, DEBOUNCE_DELAY, units, item_classes } from '../../util/constant';
import { Table, Modal, Button, Form, FloatingLabel, Row, Col } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { EditIcon } from '../icons';
import 'react-toastify/dist/ReactToastify.css';
import PaginationInPage from '../Pagination';
import useDebounce from '../useDebounce';
import { GrDocumentExcel } from 'react-icons/gr';
import { getStoredData, get_auth_key } from '../../util';

const Items = () => {
  const formFields = {
    name: '',
    hsnCode: '',
    hsnDesc: '',
    itemCode: '',
    class: '',
    unit: '',
    categoryId: '',
  };
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemList, setItemList] = useState([]);
  const [formDetails, setFormDetails] = useState(formFields);
  const [showConfirmModel, setShowConfirmModel] = useState(false);
  const [query, setQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const clickToDownload = useRef();

  const storedData = JSON.parse(getStoredData(get_auth_key()));
  const admins = ['admin', 'super-admin', 'inventory-admin'].includes(storedData.role);

  const handleShow = () => {
    setShowConfirmModel(true);
    setFormDetails(formFields);
  };

  const handleClose = () => {
    setShowConfirmModel(false);
  };

  const handleEdit = (e, id) => {
    e.preventDefault();
    try {
      get('/inv/items/?id=' + id).then((item) => {
        if (item?.data?.rows?.length) {
          const itemData = item.data.rows[0];
          setFormDetails({ ...itemData });
          setShowConfirmModel(true);
        } else {
          toast.error('Invalid Item id');
        }
      });
    } catch (err) {
      toast.error(err?.response?.data?.data?.rows?.message || 'Something went wrong');
    }
  };

  const handleChange = (event) => {
    event.preventDefault();
    let name = event.target.name;
    let value = event.target.value;
    let _formDetails = { ...formDetails, [name]: value };
    setFormDetails(_formDetails);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if ('id' in formDetails) {
      try {
        await put(formDetails, '/inv/items/' + formDetails.id);
        toast.success('Item updated successfully');
        setShowConfirmModel(false);
        getItems();
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.response?.data?.data?.message || err.message, { autoClose: 3000 });
      }
    } else {
      try {
        await post(formDetails, '/inv/items');
        toast.success('Item added successfully');
        setShowConfirmModel(false);
        getItems();
      } catch (err) {
        toast.error(err.response.data.message || err.response.data.data.message || err.message, { autoClose: 2000 });
      }
    }
  };

  function getItems(conditionType) {
    try {
      let url = '';
      const categoryParam = selectedCategoryId ? `&categoryId=${selectedCategoryId}` : '';
      if (conditionType && conditionType === 'download') {
        url = `/inv/items/?query=${query}&conditionType=download${categoryParam}`;
      } else {
        url = `/inv/items/?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}&query=${query}${categoryParam}`;
      }
      get(url).then((response) => {
        if (conditionType && conditionType === 'download') {
          clickToDownload.current.href = `${process.env.REACT_APP_BASE_URL}/file/download?filePath=${response.data.filePath}`;
          setTimeout(() => {
            clickToDownload.current.click();
          }, 500);
        } else {
          setItemList(response.data.rows);
          setTotalRecords(response.data.count ?? 0);
        }
      });
    } catch (err) {
      toast.error(err?.response?.data?.data?.message || err?.message);
    }
  }

  useDebounce(
    () => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        getItems();
      }
    },
    [query, selectedCategoryId],
    DEBOUNCE_DELAY
  );

  const handleCategoryFilter = (e) => {
    setSelectedCategoryId(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    getItems();
  }, [currentPage]);

  useEffect(() => {
    getCategories();
  }, []);

  const getCategories = async () => {
    try {
      const response = await get('/inv/item-categories');
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleSelect = (e) => {
    const { value, name } = e.target;
    setFormDetails((state) => {
      return { ...state, [name]: value };
    });
  };

  return (
    <>
      <ToastContainer />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Items | {totalRecords ?? 0}
          </h4>
          <div className="card-header-actions">
            <Form.Select
              value={selectedCategoryId}
              onChange={handleCategoryFilter}
              style={{ width: '200px', marginRight: '15px' }}
              size="sm"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="search"
              placeholder="Search by name"
              style={{ width: '250px', marginRight: '20px' }}
            />
            <GrDocumentExcel
              title="Download excel report"
              className="react-icons green"
              style={{ width: '30px', height: '30px', cursor: 'pointer' }}
              onClick={() => getItems('download')}
            />
            <a ref={clickToDownload} href="/" target="_blank" className="hiddenItem">
              HiddenClick
            </a>
            {admins && (
              <button type="button" className="btn app-btn-default dark-color " onClick={handleShow}>
                Add Item
              </button>
            )}
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>HSN Code</th>
                    <th>HSN Description</th>
                    <th>Item Code</th>
                    <th>Class</th>
                    {admins && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {itemList.map((item, index) => {
                    return (
                      <tr key={item?.id}>
                        <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                        <td>
                          {item?.name} ({item?.unit})
                        </td>
                        <td>{item?.itemCategory?.name || '-'}</td>
                        <td>{item?.hsnCode}</td>
                        <td>{item?.hsnDesc}</td>
                        <td>{item?.itemCode}</td>
                        <td>{item?.class}</td>
                        {admins && (
                          <td className="actions">
                            <span className="edit-icon" onClick={(e) => handleEdit(e, item?.id)}>
                              <EditIcon style={{ color: 'teal' }} />
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <PaginationInPage
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalRecords={totalRecords}
                TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal size="lg" show={showConfirmModel} backdrop="static" keyboard={false} className="request-modal">
        <Modal.Header>
          <Modal.Title>{'id' in formDetails ? 'Edit Item' : 'Add Item'}</Modal.Title>
          <button type="button" className="btn-close" aria-label="Close" onClick={handleClose}></button>
        </Modal.Header>
        <Modal.Body>
          <Form autoComplete="off" onSubmit={handleSubmit}>
            <Row>
              <Col xs={6}>
                <FloatingLabel label="Name" className="mb-3">
                  <Form.Control
                    className="input"
                    name="name"
                    onChange={handleChange}
                    value={formDetails.name}
                    type="text"
                    placeholder="Name"
                    required
                    autoComplete="false"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="Item Code" className="mb-3">
                  <Form.Control
                    className="input"
                    onChange={handleChange}
                    value={formDetails.itemCode}
                    name="itemCode"
                    type="text"
                    placeholder="Item Code"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="HSN Code" className="mb-3">
                  <Form.Control
                    className="input"
                    onChange={handleChange}
                    value={formDetails.hsnCode}
                    name="hsnCode"
                    type="text"
                    placeholder="HSN Code"
                    required
                  />
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="HSN Description" className="mb-3">
                  <Form.Control
                    className="input"
                    onChange={handleChange}
                    value={formDetails.hsnDesc}
                    name="hsnDesc"
                    type="text"
                    placeholder="HSN Desc"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="Unit" className="mb-3">
                  <select
                    required
                    name="unit"
                    value={formDetails.unit}
                    onChange={handleSelect}
                    aria-label="Floating label select example"
                    className="form-select"
                  >
                    <option value="">Select Unit</option>
                    {units.map((item, index) => (
                      <option key={index} value={item.value}>
                        {item?.label}
                      </option>
                    ))}
                  </select>
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="Class" className="mb-3">
                  <select
                    name="class"
                    value={formDetails.class}
                    onChange={handleSelect}
                    aria-label="Floating label select example"
                    className="form-select"
                  >
                    <option value="">Select Class</option>
                    {item_classes.map((item, index) => (
                      <option key={index} value={item.value}>
                        {item?.label}
                      </option>
                    ))}
                  </select>
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="Category" className="mb-3">
                  <select
                    name="categoryId"
                    value={formDetails.categoryId}
                    onChange={handleSelect}
                    aria-label="Floating label select example"
                    className="form-select"
                  >
                    <option value="">Select Category</option>
                    {categories.map((category, index) => (
                      <option key={index} value={category.id}>
                        {category?.name}
                      </option>
                    ))}
                  </select>
                </FloatingLabel>
              </Col>
            </Row>
            <div className="container text-center">
              <Button type="submit" className="add-request-btn mt-4 mb-3 justify-content-center">
                {'id' in formDetails ? 'Update' : 'Add'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Items;
